/**
 * What Sentry records about URLs, scrubbed on the way out, and how much it
 * traces. Kept apart from sentry.ts (which initialises Sentry on import) so
 * it can be tested on its own.
 *
 * The public pages are opened by a capability token in the path (/pay/:token,
 * /w9/:token…), and the API calls they make carry it too. The fetch spans
 * Sentry makes on its own, its breadcrumbs, the page an error happened on and
 * the pageload/navigation names all hold the raw URL; each is normalised
 * here, which also keeps one series per route rather than per record.
 */
import type * as Sentry from "@sentry/react";
import { normalizeApiPath, normalizeUrl } from "../utils/pathNormalization";

type Options = NonNullable<Parameters<typeof Sentry.init>[0]>;
type SpanJSON = Parameters<NonNullable<Options["beforeSendSpan"]>>[0];
type Breadcrumb = Parameters<NonNullable<Options["beforeBreadcrumb"]>>[0];
type ErrorEvent = Parameters<NonNullable<Options["beforeSend"]>>[0];
type TracingOptions = NonNullable<Parameters<typeof Sentry.browserTracingIntegration>[0]>;
type StartSpanOptions = Parameters<NonNullable<TracingOptions["beforeStartSpan"]>>[0];

/** Production traces a sample; everywhere else traces everything. */
export const DEFAULT_PRODUCTION_TRACES_SAMPLE_RATE = 0.1;

/**
 * REACT_APP_SENTRY_TRACES_SAMPLE_RATE when it is a number from 0 to 1,
 * otherwise 0.1 in production and 1 elsewhere. Tracing every page load and
 * API call in production made Sentry's cost and rate limit grow with traffic.
 * Errors and replays-on-error are sampled separately and stay at 100%.
 */
export const tracesSampleRateFor = (raw: string | undefined, isProduction: boolean): number => {
  const parsed = raw !== undefined && raw.trim() !== "" ? Number(raw) : NaN;
  if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 1) return parsed;
  return isProduction ? DEFAULT_PRODUCTION_TRACES_SAMPLE_RATE : 1;
};

/** `GET https://api…/w9-forms/AbC…?x=1` → `GET https://api…/w9-forms/:token`. */
const normalizeDescription = (description: string): string => {
  const match = /^([A-Z]+ )(.+)$/.exec(description);
  return match ? match[1] + normalizeUrl(match[2]) : normalizeUrl(description);
};

const URL_DATA_KEYS = ["url", "http.url", "url.full", "http.target", "url.path"];
const QUERY_DATA_KEYS = ["http.query", "http.fragment", "url.query"];

/** beforeSendSpan: the URL-bearing parts of an HTTP span, normalised. */
export const scrubSpan = (span: SpanJSON): SpanJSON => {
  if (!span.op?.startsWith("http")) return span;
  const data = { ...(span.data ?? {}) } as Record<string, unknown>;
  URL_DATA_KEYS.forEach((key) => {
    if (typeof data[key] === "string") data[key] = normalizeUrl(data[key] as string);
  });
  QUERY_DATA_KEYS.forEach((key) => {
    delete data[key];
  });
  return {
    ...span,
    description: span.description ? normalizeDescription(span.description) : span.description,
    data: data as SpanJSON["data"],
  };
};

/** beforeBreadcrumb: fetch, xhr and navigation breadcrumbs carry URLs in `data`. */
export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  if (!breadcrumb.data) return breadcrumb;
  const data = { ...breadcrumb.data } as Record<string, unknown>;
  ["url", "from", "to"].forEach((key) => {
    if (typeof data[key] === "string") data[key] = normalizeUrl(data[key] as string);
  });
  return { ...breadcrumb, data };
};

/** beforeSend: the page an error happened on, and its transaction name. */
export const scrubEvent = (event: ErrorEvent): ErrorEvent => {
  const scrubbed: ErrorEvent = { ...event };
  if (event.request) {
    const request = { ...event.request };
    if (request.url) request.url = normalizeUrl(request.url);
    delete request.query_string;
    scrubbed.request = request;
  }
  if (event.transaction?.startsWith("/")) scrubbed.transaction = normalizeApiPath(event.transaction);
  return scrubbed;
};

/** browserTracingIntegration beforeStartSpan: pageload/navigation names are the page's path. */
export const normalizeStartSpan = (options: StartSpanOptions): StartSpanOptions =>
  options.name && options.name.startsWith("/") ? { ...options, name: normalizeApiPath(options.name) } : options;
